/**
 * Renders single @graph JSON-LD structured data (design doc §9).
 * All data comes from the same PageModel - no second copy.
 */

import { escapeJsonForHtml } from "../../server/services/seo/seoEscape";
import type { TopicPageModel, ChapterPageModel } from "./pageModels";
import { buildPrimaryImageObject } from './seoImage';
import { brandConfig } from '../../config/brandConfig';
import { isPublicTopicSource } from '../../domain/topicSourcePolicy';

function siteIdentity(canonical: string) {
	const origin = new URL(canonical).origin;
	return {
		origin,
		websiteId: `${origin}/#website`,
		organization: { "@type": "Organization", "@id": `${origin}/#organization`, name: brandConfig.name, url: `${origin}/` },
	};
}

export function renderTopicStructuredData(model: TopicPageModel): string {
	const canonical = model.seo.canonical;
	const site = siteIdentity(canonical);
	const primaryImageId = `${canonical}#primaryimage`;
	const categoryBreadcrumb = model.topic.category && model.topic.categorySlug
		? [{ "@type": "ListItem", position: 2, name: model.topic.category, item: `${site.origin}/browse?${new URLSearchParams({ category: model.topic.categorySlug, sort: 'newest' }).toString()}` }]
		: [];
	const topicBreadcrumbPosition = categoryBreadcrumb.length ? 3 : 2;
	const graph: unknown[] = [
		{
			"@type": "WebPage",
			"@id": `${canonical}#webpage`,
			url: canonical,
			name: model.topic.title,
			description: model.topic.metaDescription,
			isPartOf: { "@id": site.websiteId },
			primaryImageOfPage: { '@id': primaryImageId },
		},
		{
			"@type": "Course",
			"@id": `${canonical}#course`,
			url: canonical,
			name: model.topic.title,
			description: model.topic.metaDescription,
			abstract: model.topic.summary,
			...(model.topic.keyTakeaways.length ? { teaches: model.topic.keyTakeaways } : {}),
			...(model.topic.keywords.length ? { keywords: model.topic.keywords.map((k) => k.keyword) } : {}),
			...(model.topic.keywords.length ? { about: model.topic.keywords.map((k) => ({ "@type": "Thing", name: k.keyword })) } : {}),
			image: { '@id': primaryImageId },
			provider: site.organization,
			hasPart: model.chapters.map((ch) => ({
				"@type": "LearningResource",
				name: ch.title,
				url: `${canonical}/chapters/${ch.index}`,
			})),
		},
		{
			"@type": "BreadcrumbList",
			itemListElement: [
				{ "@type": "ListItem", position: 1, name: brandConfig.name, item: `${site.origin}/` },
				...categoryBreadcrumb,
				{ "@type": "ListItem", position: topicBreadcrumbPosition, name: model.topic.title, item: canonical },
			],
		},
		buildPrimaryImageObject(model.seo, model.topic.title),
	];

	if (model.topic.faqs.length > 0) {
		graph.push({
			"@type": "FAQPage",
			mainEntity: model.topic.faqs.map((f) => ({
				"@type": "Question",
				name: f.question,
				acceptedAnswer: { "@type": "Answer", text: f.answer },
			})),
		});
	}

	return escapeJsonForHtml(JSON.stringify({ "@context": "https://schema.org", "@graph": graph }));
}

export function renderChapterStructuredData(model: ChapterPageModel): string {
	const canonical = model.seo.canonical;
	const site = siteIdentity(canonical);
	const topicCanonical = canonical.replace(/\/chapters\/\d+$/, "");
	const primaryImageId = `${canonical}#primaryimage`;

	const graph: unknown[] = [
		{
			"@type": "WebPage",
			"@id": `${canonical}#webpage`,
			url: canonical,
			name: model.chapter.title,
			description: model.chapter.metaDescription,
			isPartOf: { "@id": `${topicCanonical}#course` },
			primaryImageOfPage: { '@id': primaryImageId },
		},
		{
			"@type": ["Article", "LearningResource"],
			"@id": `${canonical}#article`,
			url: canonical,
			headline: model.chapter.title,
			description: model.chapter.metaDescription,
			abstract: model.chapter.summary,
			...(model.chapter.keywords.length ? { keywords: model.chapter.keywords.map((k) => k.keyword) } : {}),
			...(model.chapter.keyTakeaways.length ? { teaches: model.chapter.keyTakeaways } : {}),
			datePublished: model.chapter.createdAt,
			dateModified: model.chapter.updatedAt,
			image: { '@id': primaryImageId },
			mainEntityOfPage: { "@id": `${canonical}#webpage` },
			isPartOf: { "@id": `${topicCanonical}#course` },
			...(isPublicTopicSource(model.topic.source) ? { author: site.organization } : {}),
			publisher: site.organization,
			isAccessibleForFree: model.access.publiclyAccessible,
		},
		{
			"@type": "BreadcrumbList",
			itemListElement: [
				{ "@type": "ListItem", position: 1, name: brandConfig.name, item: `${site.origin}/` },
				{ "@type": "ListItem", position: 2, name: model.topic.title, item: topicCanonical },
				{ "@type": "ListItem", position: 3, name: `Chapter ${model.chapter.index}: ${model.chapter.title}`, item: canonical },
			],
		},
		buildPrimaryImageObject(model.seo, model.chapter.title),
	];

	if (model.chapter.faqs.length > 0) {
		graph.push({
			"@type": "FAQPage",
			mainEntity: model.chapter.faqs.map((f) => ({
				"@type": "Question",
				name: f.question,
				acceptedAnswer: { "@type": "Answer", text: f.answer },
			})),
		});
	}

	return escapeJsonForHtml(JSON.stringify({ "@context": "https://schema.org", "@graph": graph }));
}
